import type { AiPrintoutData } from "../../../downloads/aiPrintout/contract";
import type { AiPrintoutDesignContext } from "../../../downloads/aiPrintout/designContext";
import { buildDesignContext } from "../../../downloads/aiPrintout/designContext";
import type { CustomPrintoutDraft, RepairRequest } from "./customPrintoutDraftState";
import type {
	CustomPrintoutCoverage,
	CustomPrintoutDetail,
	CustomPrintoutEffort,
	CustomPrintoutRepair,
	StartCustomPrintoutGenerationRequest,
} from "./customPrintoutTypes";

/** One design request, before the attached example is uploaded. */
export type CustomPrintoutGenerationRequest = {
	printoutId: string | null;
	prompt: string | null;
	/** Uploaded first through the existing example-file route, then referenced by its key. */
	file: File | null;
	designContext: AiPrintoutDesignContext;
	baseVersionId: string | null;
	baseGenerationId: string | null;
	repair: CustomPrintoutRepair | null;
	effort: CustomPrintoutEffort;
	/** The user confirmed this printout may be a summary: they answered Annie's question, or the base is one. */
	allowsSummary: boolean;
};

type SubmitInput = {
	printoutId: string | null;
	/** The composer's words, as typed. */
	prompt: string;
	file: File | null;
	/** Set by Fix with Annie: the failure this request is about. */
	fixRepair: CustomPrintoutRepair | null;
	/** The composer's thinking level. */
	effort: CustomPrintoutEffort;
	draft: CustomPrintoutDraft | null;
	saved: CustomPrintoutDetail | null;
	/** A new printout's starting point: the saved printout it copies, or `null` for the reference sheet. */
	startFrom: CustomPrintoutDetail | null;
	data: AiPrintoutData;
};

/** The saved version a request refines when there is no draft: the shown printout's, else the one a new printout copies. */
const savedBaseOf = (saved: CustomPrintoutDetail | null, startFrom: CustomPrintoutDetail | null): string | null => {
	if (saved !== null) return saved.versionId;
	return startFrom === null ? null : startFrom.versionId;
};

/**
 * What the composer sends: it refines the draft if there is one, else the saved version (or, for a new printout, the
 * saved printout it starts from), else starts fresh.
 */
export const submitRequestOf = ({
	printoutId,
	prompt,
	file,
	fixRepair,
	effort,
	draft,
	saved,
	startFrom,
	data,
}: SubmitInput): CustomPrintoutGenerationRequest => {
	const request = prompt.trim();
	const baseGenerationId = draft === null ? null : draft.generationId;
	const baseVersionId = baseGenerationId === null ? savedBaseOf(saved, startFrom) : null;
	return {
		printoutId,
		prompt: request === "" ? null : request,
		file,
		designContext: buildDesignContext(data),
		baseVersionId,
		baseGenerationId,
		// A repair is sent with the template it repairs; a brand-new printout has none yet.
		repair: baseGenerationId === null && baseVersionId === null ? null : fixRepair,
		effort,
		allowsSummary: coverageOfBase(draft, saved, startFrom) === "summary",
	};
};

/** What the template a request refines shows: the draft's, else the saved version's or the copied one's; a new sheet lists everyone. */
const coverageOfBase = (
	draft: CustomPrintoutDraft | null,
	saved: CustomPrintoutDetail | null,
	startFrom: CustomPrintoutDetail | null,
): CustomPrintoutCoverage => {
	if (draft !== null) return draft.coverage;
	if (saved !== null) return saved.coverage;
	return startFrom === null ? "every_shift" : startFrom.coverage;
};

/**
 * The automatic repair of the draft `baseGenerationId`: no words, no example — only what failed, at the thinking
 * level that draft was designed at.
 */
export const repairRequestOf = (
	printoutId: string | null,
	data: AiPrintoutData,
	{ baseGenerationId, repair, effort, allowsSummary }: RepairRequest,
): CustomPrintoutGenerationRequest => ({
	printoutId,
	prompt: null,
	file: null,
	designContext: buildDesignContext(data),
	baseVersionId: null,
	baseGenerationId,
	repair,
	effort,
	allowsSummary,
});

/** The API body: every key the strict server model has (optional ones as `null`), and nothing else. */
export const generationRequestBody = (
	departmentId: string,
	request: CustomPrintoutGenerationRequest,
	fileKey: string | null,
): StartCustomPrintoutGenerationRequest => ({
	departmentId,
	printoutId: request.printoutId,
	prompt: request.prompt,
	fileKey,
	designContext: request.designContext,
	baseVersionId: request.baseVersionId,
	baseGenerationId: request.baseGenerationId,
	repair: request.repair,
	effort: request.effort,
	allowsSummary: request.allowsSummary,
});
